import { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddCompanyModal as AddSecondCompanyModal } from '~/components/@sibill-components/AutomaticallyAddSecondCompany/AddCompanyModal';
import Button from '~/components/buttons/NewButton';
import Modal from '~/components/modals/Modal/Modal';
import ModalActions from '~/components/modals/Modal/ModalActions';
import ModalContent from '~/components/modals/Modal/ModalContent';
import ModalTitle from '~/components/modals/Modal/ModalTitle';
import Text from '~/components/typographies/Text';
import { snack } from '~/hooks/ToastProvider';
import { useSwitchCompany } from '~/hooks/useActiveCompany';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import { useHasAutomaticSecondCompanyOnboarding } from '~/hooks/useFeatureFlag';
import AddStoreIcon from '~/icons/icon-store-add.svg?react';
import { ModalProps } from '~/modals/Modal';
import { RouteConfig } from '~/routes/routeConfig';
import { useCompanyCreateMutation, useCompanyJoinMutation } from '~/services/company';
import { FiscalRegime, UserRole } from '~/types/company';
import { JSONApiErrorResponse } from '~/types/jsonapi';
import { isAxiosError } from '~/utils/helpers';
import { firstJSONApiError } from '~/utils/jsonapi-errors';
import AddCompanyForm, { CompanyData } from './AddCompanyForm';
import styles from './AddCompanyModal.module.scss';
import AddCompanySearch from './AddCompanySearch';
import CompanySummary, { CompanySummaryData } from './CompanySummary';

enum AddCompanyPhase {
  Identify,
  Join,
  Add,
  Create,
  Message,
}

type AddCompanyState =
  | {
      phase: AddCompanyPhase.Identify;
      taxNumber: string;
    }
  | {
      phase: AddCompanyPhase.Join;
      company: CompanySummaryData;
    }
  | {
      phase: AddCompanyPhase.Add;
      company: CompanySummaryData;
    }
  | {
      phase: AddCompanyPhase.Create;
      taxNumber: string;
    }
  | {
      phase: AddCompanyPhase.Message;
      message: string;
    };

const AddCompanyLayout = ({
  children,
  onOpenChange,
  title,
}: PropsWithChildren<{ onOpenChange: (isOpen: boolean) => void; title: string }>) => {
  return (
    <Modal className={styles.modal} isOpen onOpenChange={onOpenChange}>
      <ModalTitle Icon={AddStoreIcon} onCloseClick={() => onOpenChange(false)}>
        {title}
      </ModalTitle>
      <div className={styles.content}>
        <div className={styles.form}>
          <ModalContent className={styles.modalContent}>{children}</ModalContent>
        </div>
      </div>
    </Modal>
  );
};

const AddCompany = (props: ModalProps) => {
  const { t } = useTranslation('translations');
  const { t: ta } = useTranslation('auth', { keyPrefix: 'signup.add_company' });

  const { captureException } = useExceptionLogger();
  const companyCreateMutation = useCompanyCreateMutation();
  const companyJoinMutation = useCompanyJoinMutation();
  const switchCompany = useSwitchCompany();

  const [state, setState] = useState<AddCompanyState>({
    phase: AddCompanyPhase.Identify,
    taxNumber: '',
  });

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      props.closeModal();
    }
  };

  const createCompany = async (data: CompanyData) => {
    try {
      const company = await companyCreateMutation.mutateAsync({
        name: data.name,
        vatNumber: data.vatNumber,
        taxNumber: data.taxNumber,
        fiscalRegime: data.fiscalRegime,
        userRole: UserRole.ENTREPRENEUR,
        leadSource: undefined,
        companyIdentity: {
          lid: 'fake-id',
          address: data.address,
          city: data.city,
          country: data.country,
          addressCountry: data.country,
          postalCode: data.postalCode,
          vatNumber: data.vatNumber,
          taxNumber: data.taxNumber,
          companyName: data.name,
        },
      });
      switchCompany(company, RouteConfig.SignUpPlanSummary.buildLink());

      props.closeModal();
    } catch (err) {
      captureException(err, {
        extra: {
          name: data.name,
          address: data.address,
          city: data.city,
          country: data.country,
          addressCountry: data.country,
          postalCode: data.postalCode,
          vatNumber: data.vatNumber,
          taxNumber: data.taxNumber,
        },
      });
      snack.error(ta('error_messages.company_creation_error'));
    }
  };

  const addCompany = async (data: CompanySummaryData) => {
    try {
      const { companyIdentityId, ...others } = data;
      const company = await companyCreateMutation.mutateAsync({
        ...others,
        fiscalRegime: FiscalRegime.RF01,
        companyIdentity: { id: companyIdentityId },
      });

      switchCompany(company, RouteConfig.AppRoot.buildLink());

      props.closeModal();
    } catch (error) {
      captureException(error, {
        extra: {
          company_name: data.name,
          vat_number: data.vatNumber,
          tax_number: data.taxNumber,
        },
      });
      snack.error(t('ADD_COMPANY_ERROR_MESSAGE'));
    }
  };

  const joinCompany = async (data: CompanySummaryData) => {
    try {
      await companyJoinMutation.mutateAsync({
        userRole: UserRole.EMPLOYEE,
        taxNumber: data.taxNumber,
      });
      setState({ phase: AddCompanyPhase.Message, message: t('JOIN_COMPANY_ALREADY_PRESENT_MESSAGE') });
    } catch (error) {
      captureException(error, {
        extra: {
          company_name: data.name,
          tax_number: data.taxNumber,
        },
      });
      if (
        isAxiosError<JSONApiErrorResponse>(error) &&
        error.response?.status === 422 &&
        firstJSONApiError(error)?.detail === 'has already been taken'
      ) {
        snack.error(t('JOIN_COMPANY_ALREADY_EXISTS_ERROR_MESSAGE'));
      } else {
        snack.error(t('JOIN_COMPANY_ERROR_MESSAGE'));
      }
    }
  };

  switch (state.phase) {
    case AddCompanyPhase.Identify:
      return (
        <AddCompanyLayout onOpenChange={handleOpenChange} title={t('ADD_COMPANY')}>
          <AddCompanySearch
            initialValue={state.taxNumber}
            onNotFound={(taxNumber) => setState({ phase: AddCompanyPhase.Create, taxNumber })}
            onFound={(taxNumber, companyIdentity, available) => {
              const companySummaryData = {
                name: companyIdentity.companyName || '',
                vatNumber: companyIdentity.vatNumber || '',
                taxNumber: companyIdentity.taxNumber,
                companyIdentityId: companyIdentity.id,
              };
              if (available) {
                setState({ phase: AddCompanyPhase.Add, company: companySummaryData });
              } else {
                setState({ phase: AddCompanyPhase.Join, company: companySummaryData });
              }
            }}
          />
        </AddCompanyLayout>
      );

    case AddCompanyPhase.Add:
      return (
        <AddCompanyLayout onOpenChange={handleOpenChange} title={t('ADD_COMPANY')}>
          <CompanySummary
            onCancel={() => {
              setState({ phase: AddCompanyPhase.Identify, taxNumber: state.company.taxNumber });
            }}
            initialValue={state.company}
            onSubmit={addCompany}
          />
        </AddCompanyLayout>
      );

    case AddCompanyPhase.Join:
      return (
        <AddCompanyLayout onOpenChange={handleOpenChange} title={t('ADD_COMPANY')}>
          <CompanySummary
            onCancel={() => {
              setState({ phase: AddCompanyPhase.Identify, taxNumber: state.company.taxNumber });
            }}
            initialValue={state.company}
            onSubmit={joinCompany}
          />
        </AddCompanyLayout>
      );

    case AddCompanyPhase.Create:
      return (
        <AddCompanyLayout onOpenChange={handleOpenChange} title={t('ADD_COMPANY')}>
          <AddCompanyForm
            initialValues={{
              country: 'IT',
              taxNumber: state.taxNumber || '',
            }}
            onCancel={() => {
              setState({ phase: AddCompanyPhase.Identify, taxNumber: state.taxNumber });
            }}
            vatNumberDisabled={false}
            onSubmit={createCompany}
          />
        </AddCompanyLayout>
      );

    case AddCompanyPhase.Message:
      return (
        <AddCompanyLayout onOpenChange={handleOpenChange} title={t('ADD_COMPANY')}>
          <Text variant="body1" weight="regular" className={styles.message}>
            {state.message}
          </Text>
          <ModalActions>
            <Button onClick={() => handleOpenChange(false)}>{t('CONFIRM')}</Button>
          </ModalActions>
        </AddCompanyLayout>
      );
  }
};
const AddCompanyModal = (props: ModalProps) => {
  const hasAutomaticSecondCompanyOnboarding = useHasAutomaticSecondCompanyOnboarding();

  if (hasAutomaticSecondCompanyOnboarding) {
    return <AddSecondCompanyModal {...props} />;
  }

  return <AddCompany {...props} />;
};

export default AddCompanyModal;
